<script setup lang="ts">
/**
 * Directors' Circular Resolution (Update/Add Business Address) — M32018, Section 47(2).
 * · sole / multiple director + Constitution / Articles / Third Schedule
 * · BUSINESS ADDRESS (신규/추가 주소)  (2) records 를 등록주소 외 신주소에 보관
 */
import { computed, inject } from 'vue';
import { UPDATE_BUSINESS_ADDRESS_DOC_CTX } from './updateBusinessAddressContext';
import { RECORD_ITEMS } from './updateBusinessAddressFields';
import UpdateBusinessAddressDocVarField from './UpdateBusinessAddressDocVarField.vue';
import UpdateBusinessAddressSigners from './parts/UpdateBusinessAddressSigners.vue';

const ctx = inject(UPDATE_BUSINESS_ADDRESS_DOC_CTX)!;
const isMultiple = computed(() => ctx.flag('is_multiple_director_company'));
const basis = computed(() => ctx.choiceOf('resolution_basis') || 'constitution');

function cycleBasis() {
  const next = basis.value === 'constitution' ? 'articles' : basis.value === 'articles' ? 'third_schedule' : 'constitution';
  ctx.setChoice('resolution_basis', next);
}
</script>

<template>
  <div class="uba-doc">
    <div class="doc-head">
      <div class="doc-meta">Company Registration No: <UpdateBusinessAddressDocVarField field-key="company_registration_no" /></div>
      <div class="doc-company"><UpdateBusinessAddressDocVarField field-key="company_name" /></div>
      <div class="doc-meta italic">(Incorporated in Malaysia)</div>
    </div>

    <div class="dcr-title">DIRECTORS' CIRCULAR RESOLUTION</div>

    <p class="doc-p">
      <span
        class="doc-toggle"
        title="sole / multiple director"
        @click="ctx.toggleFlag('is_multiple_director_company')"
      >{{ isMultiple ? 'We, the undersigned, being the directors' : 'I, the undersigned, being the sole director' }}</span>
      of the above-mentioned Company for the time being, do resolve the following in writing pursuant to
      <template v-if="basis === 'third_schedule'">
        <span class="doc-toggle" title="resolution basis" @click="cycleBasis">Paragraph 15 of the Third Schedule of the Companies Act 2016</span>.
      </template>
      <template v-else>
        <span class="doc-toggle" title="resolution basis" @click="cycleBasis">{{ basis === 'articles' ? 'Article' : 'Clause' }}</span>
        <UpdateBusinessAddressDocVarField field-key="constitution_clause_number" />
        <template v-if="basis === 'articles'"> of the Company's Constitution (Articles of Association)</template>
        <template v-else> of the Company's Constitution</template>.
      </template>
    </p>

    <p class="doc-sec-title">BUSINESS ADDRESS</p>
    <p class="doc-p">That the Company's business address shall be located at: -</p>
    <p class="doc-addr">
      <UpdateBusinessAddressDocVarField field-key="address_line_1" /><br />
      <UpdateBusinessAddressDocVarField field-key="address_line_2" /><br />
      <UpdateBusinessAddressDocVarField field-key="address_line_3" /><br />
      <UpdateBusinessAddressDocVarField field-key="postcode" /> <UpdateBusinessAddressDocVarField field-key="city" /><br />
      <UpdateBusinessAddressDocVarField field-key="state" />, <UpdateBusinessAddressDocVarField field-key="country" />
    </p>

    <p class="doc-sec-title">(2) NOTIFICATION WHERE REGISTERS OR RECORDS ARE NOT KEPT AT THE REGISTERED ADDRESS</p>
    <p class="doc-p">
      THAT the following records be kept at
      <UpdateBusinessAddressDocVarField field-key="address_line_1" />,
      <UpdateBusinessAddressDocVarField field-key="address_line_2" />,
      <UpdateBusinessAddressDocVarField field-key="address_line_3" />,
      <UpdateBusinessAddressDocVarField field-key="postcode" /> <UpdateBusinessAddressDocVarField field-key="city" />,
      <UpdateBusinessAddressDocVarField field-key="state" />, <UpdateBusinessAddressDocVarField field-key="country" />
      :-
    </p>
    <ul class="rec-list">
      <li
        v-for="r in RECORD_ITEMS" :key="r.key"
        class="rec-item doc-toggle" :class="{ off: !ctx.flag(r.key) }"
        :title="ctx.flag(r.key) ? 'included — click to exclude' : 'excluded — click to include'"
        @click="ctx.toggleFlag(r.key)"
      >
        <span class="rec-mark">{{ ctx.flag(r.key) ? '☑' : '☐' }}</span>
        <span class="rec-tx">{{ r.text }} ;</span>
      </li>
    </ul>

    <p class="doc-p">
      THAT the Secretary of the Company be and is hereby authorised to lodge the necessary notification to the
      Companies Commission of Malaysia (CCM) accordingly.
    </p>

    <p class="doc-lbl-title">BOARD OF DIRECTORS</p>
    <UpdateBusinessAddressSigners var-key="dcr" board-label="BOARD OF DIRECTORS" />
    <p class="doc-p" style="margin-top:14px"><b>Date:</b> <UpdateBusinessAddressDocVarField field-key="dcr_date" /></p>
  </div>
</template>

<style src="./updateBusinessAddressDoc.css"></style>
